/**
 * Member-facing alert presentation.
 *
 * Severity and guidance come from the notifications API
 * (`extra_data.member_severity` / `member_guidance`). Needle classification
 * lives only in the backend policy; this module does not keep a twin list.
 */

export type MemberAlertSeverity = "info" | "warning" | "critical";

export interface MemberAlertPresentation {
  severity: MemberAlertSeverity;
  guidance: string;
  toast: boolean;
  variant: "destructive" | "warning" | "info";
}

const ACTION_KINDS = new Set(["reauth", "identity_mismatch"]);

// Ledger-lag advisory. Backend publish_alert uses alert_type="risk", which
// would otherwise collapse to critical ("New trades are paused") with no halt.
const OWNERSHIP_ADVISORY_KINDS = new Set(["cash_exit_ownership_unproven"]);
const CLOSE_REASON_KINDS = new Set(["account_close_reason"]);
const PM_REBALANCE_KIND = "pm_rebalance";

const PM_WATCH_OF = 3;

function pmWatchDay(value: unknown): number {
  const n = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(n) || n < 0) return 0;
  return Math.min(Math.trunc(n), PM_WATCH_OF);
}

const PM_GUIDANCE: Record<string, string> = {
  applied:
    "Sigma changed how much of your desk each strategy gets for the current market. The new weights are already in effect. You do not need to reconnect or flip a group switch.",
  gated:
    "Sigma wanted to rebalance but a safety bound held the book, so nothing changed. Review the proposed weights here. You do not need to reconnect or enable a group.",
};

type AlertLike = {
  notification_type?: string | null;
  title?: string | null;
  message?: string | null;
  priority?: string | null;
  extra_data?: Record<string, unknown> | null;
  metadata?: Record<string, unknown> | null;
  severity?: string | null;
};

function haystack(title: string, message: string): string {
  return `${title} ${message}`.trim().toLowerCase();
}

function metaOf(alert: AlertLike): Record<string, unknown> {
  const extra =
    alert.extra_data && typeof alert.extra_data === "object"
      ? alert.extra_data
      : {};
  const metadata =
    alert.metadata && typeof alert.metadata === "object"
      ? alert.metadata
      : {};
  return { ...extra, ...metadata };
}

function asString(value: unknown): string {
  return typeof value === "string" ? value.trim().toLowerCase() : "";
}

export function classifyMemberAlertSeverity(
  alert: AlertLike,
): MemberAlertSeverity {
  const meta = metaOf(alert);
  const stored = asString(meta.member_severity);
  if (stored === "info" || stored === "warning" || stored === "critical") {
    return stored;
  }

  const ntype = (alert.notification_type || "").toLowerCase();
  const prio = (alert.priority || alert.severity || "").toLowerCase();
  if (
    OWNERSHIP_ADVISORY_KINDS.has(asString(meta.kind)) ||
    CLOSE_REASON_KINDS.has(asString(meta.kind))
  ) {
    return "warning";
  }
  if (asString(meta.kind) === PM_REBALANCE_KIND) {
    return "info";
  }
  if (ntype === "emergency" || ntype === "risk") {
    return "critical";
  }
  if (
    ntype === "critical_alert" ||
    prio === "critical" ||
    prio === "urgent" ||
    prio === "high"
  ) {
    return "warning";
  }
  return "info";
}

export function memberAlertGuidance(
  alert: AlertLike,
  severity?: MemberAlertSeverity,
): string {
  const meta = metaOf(alert);
  const stored =
    typeof meta.member_guidance === "string"
      ? meta.member_guidance.trim()
      : "";
  if (stored) return stored;

  const resolved = severity ?? classifyMemberAlertSeverity(alert);
  const kind = asString(meta.kind);
  const text = haystack(alert.title || "", alert.message || "");

  if (kind === PM_REBALANCE_KIND) {
    if (stored) return stored;
    const status = asString(meta.status);
    if (status === "applied") return PM_GUIDANCE.applied;
    if (status === "gated") return PM_GUIDANCE.gated;
    const day = pmWatchDay(meta.traded_days);
    return (
      `Sigma recorded a proposal. It stays on file. Sigma is watching day ${day} of ${PM_WATCH_OF}. ` +
      "Live weights are unchanged. You do not need to reconnect or enable a group."
    );
  }
  if (OWNERSHIP_ADVISORY_KINDS.has(kind)) {
    return (
      "Cash-exit protection covers copied positions only. New trades are " +
      "not paused. You do not need to enable a trading group."
    );
  }
  if (CLOSE_REASON_KINDS.has(kind)) {
    const mechanism = asString(meta.mechanism);
    if (mechanism === "cash_exit" || mechanism === "profit_protection") {
      return (
        "Sigma closed this for you. You do not need to do anything — " +
        "the gain is locked."
      );
    }
    if (mechanism === "manual") {
      return (
        "This close came from your broker login, not Cash Exit or " +
        "Profit Protection."
      );
    }
    if (mechanism === "broker_close") {
      return (
        "This close happened at the broker. The source is unknown — it was " +
        "not identified as your login and no SigmaVue closer named it."
      );
    }
    if (mechanism === "copy_mirror") {
      return "The copier closed this because the leader closed. You do not need to do anything.";
    }
    if (mechanism === "news_window") {
      return "A news-window protective close flattened this account. New entries stay managed by the desk.";
    }
    return "An account closed. This is a status update, not a trading halt.";
  }
  if (resolved === "critical") {
    return (
      "New trades are paused to protect your accounts. Open positions stay " +
      "managed. You do not need to enable a trading group — we resume when " +
      "the halt clears."
    );
  }
  if (
    ACTION_KINDS.has(kind) ||
    text.includes("reconnect") ||
    text.includes("expired")
  ) {
    return (
      "Sign back in to your broker to resume live trading. Open Accounts " +
      "and tap Reconnect. Enabling a group will not fix this."
    );
  }
  if (resolved === "info") {
    return (
      "You do not need to do anything. Nothing is wrong with your accounts " +
      "— this is an automatic status update. Enable a group from the group " +
      "card Active switch if you want it to trade; this alert does not block that."
    );
  }
  return (
    "This is a status update, not a broken account. If a reconnect button " +
    "is shown, use it; otherwise you can wait. The group Active switch still works."
  );
}

export function presentMemberAlert(alert: AlertLike): MemberAlertPresentation {
  const severity = classifyMemberAlertSeverity(alert);
  const guidance = memberAlertGuidance(alert, severity);
  return {
    severity,
    guidance,
    toast: severity !== "info",
    variant:
      severity === "critical"
        ? "destructive"
        : severity === "warning"
          ? "warning"
          : "info",
  };
}
